<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import CredentialManagementTable from "./CredentialManagementTable.svelte";
  import CredentialManagementModal from "./CredentialManagementModal.svelte";
  import CredentialManagementDeleteModal from "./CredentialManagementDeleteModal.svelte";
  import Footer from "@retailer/svelte/LimitedOrderingProductRequests/Footer.svelte";
  import Flash from "@shared/services/flashv2.js";

  // services
  import {
    addCredentials,
    removeCredentials,
  } from "@retailer/services/credential_management_service";

  // stores
  import { CredentialManagementStore } from "@retailer/stores/CredentialManagementStore";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";

  // types
  import type { PaginationDetails } from "@shared/types/Pagination";
  import type { DistributorCredentialData } from "@shared/types/DistributorCredential";

  export let pagination: PaginationDetails;

  let showModal: boolean = false;
  let showDeleteModal: boolean = false;
  let isSubmitting: boolean = false;
  let distributorNameToRemove: string = "";
  let credentialIdToRemove: number = null;
  let isRemoving: boolean = false;

  const OpenModal = () => {
    showModal = true;
  };

  const closeModal = () => {
    showModal = false;
  };

  const addDistributorCredentials = (data: DistributorCredentialData) => {
    isSubmitting = true;
    const successCallback = () => {
      window["stateService"].reload();
      isSubmitting = false;
      closeModal();
      Flash.now({ type: "success", header: "Distributor credentials added!", small: true });
    };
    const errorCallback = res => {
      isSubmitting = false;
      closeModal();
      Flash.now({
        type: "error",
        header: "An error occurred while attempting to add Distributor credentials.",
        message: res ? res.error : "",
      });
    };
    addCredentials(data, successCallback, errorCallback);
  };

  const openDeleteModal = (credentialId, distributorName) => {
    showDeleteModal = true;
    distributorNameToRemove = distributorName;
    credentialIdToRemove = credentialId;
  };

  const closeDeleteModal = () => {
    showDeleteModal = false;
    distributorNameToRemove = "";
    credentialIdToRemove = null;
  };

  const removeDistributorCredentials = () => {
    isRemoving = true;
    const successCallback = () => {
      window["stateService"].reload();
      isRemoving = false;
      closeDeleteModal();
      Flash.now({
        type: "success",
        header: "Distributor credentials have been removed!",
        small: true,
      });
    };
    const errorCallback = res => {
      isRemoving = false;
      closeDeleteModal();
      Flash.now({
        type: "error",
        header: "An error occurred while attempting to remove Distributor credentials.",
        message: res ? res.error : "",
      });
    };
    removeCredentials(credentialIdToRemove, successCallback, errorCallback);
  };
</script>

<div class="fizz-styles" style="height: 100%;">
  <div class="background-grayLight" style="height: 100%;">
    <div class="fizz-container background-white">
      <div
        class="fizz-border-bottom fizz-full-width fizz-inset-vertical-32"
        class:fizz-flex-row={$LayoutStore.isDesktop}
        class:fizz-space-between={$LayoutStore.isDesktop}
        class:fizz-flex-vertical={$LayoutStore.isMobile}
      >
        <div
          class:fizz-inset-horizontal-32={$LayoutStore.isDesktop}
          class:fizz-inset-horizontal-24={$LayoutStore.isMobile}
        >
          <p class="fizz-heading-4 fizz-text-body fizz-stack-8">Distributor Credentials</p>
          <p class="fizz-text-subdued" class:fizz-stack-32={$LayoutStore.isMobile}
            >View and manage order credentials for all Distributors</p
          >
          {#if $LayoutStore.isMobile}
            <button
              on:click={OpenModal}
              class="fizz-inline-16 fizz-button-secondary fizz-flex-row fizz-gap-0 fizz-justify-center fizz-full-width"
            >
              <Icon extraClassNames="fizz-inline" type="Plus" />
              <span>Add Distributor</span>
            </button>
          {/if}
        </div>
        {#if $LayoutStore.isDesktop}
          <button
            on:click={OpenModal}
            class="fizz-inline-16 fizz-button-secondary fizz-flex-row fizz-gap-0"
          >
            <Icon extraClassNames="fizz-inline" type="Plus" />
            <span>Add Distributor</span>
          </button>
        {/if}
      </div>
      {#if $CredentialManagementStore.credentials && $CredentialManagementStore.credentials.length > 0}
        <CredentialManagementTable {pagination} {openDeleteModal} />
      {:else}
        <div
          class="fizz-grid-center fizz-text-center fizz-inset-vertical-48"
          id="credential-management-empty-state"
        >
          <Icon
            type="Containers"
            extraClassNames="fizz-inset-vertical-16"
            width={171}
            height={150}
          />
          <p class="fizz-heading-3 fizz-text-body fizz-stack-8">No Distributors</p>
          <p
            >Here you will find Distributors and their credentials for orders to be transferred to
            their systems.</p
          >
          <button on:click={OpenModal} class="fizz-button-text">Add Distributor</button>
        </div>
      {/if}
      <Footer />
    </div>
  </div>
  <CredentialManagementModal {showModal} {closeModal} {isSubmitting} {addDistributorCredentials} />
  <CredentialManagementDeleteModal
    {showDeleteModal}
    {closeDeleteModal}
    {isRemoving}
    {distributorNameToRemove}
    {removeDistributorCredentials}
  />
</div>
